import auth0 from 'auth0-js';
import {
    get, isNil, isUndefined, omitBy,
} from 'lodash';

import URLSearchParams from '@ungap/url-search-params';
import setting from '../../config/settings';
import Logger from '../services/logger';
import BackendConnector from './backendConnector';
import LocalStorageService from '../services/LocalStorageService';
import {
    newRelicClearUserData,
} from '../services/NewRelicTrackingService';

const { localStorage } = window;

export const AUTH0_ERROR_CODE_USER_EXIST = 'user_exists';
const cookiePostfix = `_${ENVIRONMENT}`;

const setCookie = (name, value, options = {}) => {
    const optionsWithPath = {
        path: '/',
        ...options,
    };

    let updatedCookie = `${encodeURIComponent(name)}${cookiePostfix}=${encodeURIComponent(value)}`;

    // eslint-disable-next-line guard-for-in
    for (const optionKey in optionsWithPath) {
        updatedCookie += `; ${optionKey}`;
        const optionValue = optionsWithPath[optionKey];
        if (optionValue !== true) {
            updatedCookie += `=${optionValue}`;
        }
    }

    document.cookie = updatedCookie;
};

const defaultAuthData = {
    clientID: setting.Auth0ClientId,
    domain: setting.Auth0Domain,
    responseType: setting.Auth0ResponseType,
    redirectUri: setting.Auth0RedirectUrl || window.location.origin,
    leeway: setting.Auth0Leeway,
};

export default class AuthenticationHttpService {
    static auth0Authentication = new auth0.Authentication(defaultAuthData);

    static dbAuth0 = new auth0.WebAuth({ ...defaultAuthData });

    static getAuth0 = (authData = {}) => {
        const data = get(authData, 'data', {});
        if (authData?.authType === 'sso') {
            return new auth0.WebAuth(data);
        }
        return this.getAuthClient(data);
    };

    static getAuthClient = (authData) => {
        const data = omitBy(authData, isNil);

        // Because we have different client data for US and EU tenant
        //  we spread the data object after default data and make sure we are using correct client credentials
        return new auth0.Authentication({ ...defaultAuthData, ...data });
    };

    static setAuthCookie(value, expiresIn = 60 * 60 * 24) {
        const isRemoteHost = !(window.location.hostname.indexOf('localhost') > -1);
        const domain = window.location.hostname.split('.')
            .slice(-2)
            .join('.');

        setCookie(
            'authResult',
            value,
            {
                secure: isRemoteHost,
                sameSite: 'strict',
                'Max-Age': expiresIn,
                domain,
            },
        );
    }

    static setCookie(key, value, expiresIn = 60 * 60 * 24) {
        const isRemoteHost = !(window.location.hostname.indexOf('localhost') > -1);
        const domain = window.location.hostname.split('.')
            .slice(-2)
            .join('.');

        setCookie(
            key,
            value,
            {
                secure: isRemoteHost,
                sameSite: 'strict',
                'Max-Age': expiresIn,
                domain,
            },
        );
    }

    static removeCookie(key) {
        this.setCookie(key, '', 0);
    }

    static loginV2({ token, expiresInSec, employerId }) {
        return BackendConnector.post('/login', {
            token,
            expiresIn: expiresInSec,
            employerId,
        });
    }

    static login({
        username,
        password,
        authData,
    }) {
        return new Promise((resolve, reject) => {
            this.getAuth0(authData).login({
                realm: 'Username-Password-Authentication',
                username,
                password,
            }, (err, authResult) => {
                if (authResult?.idToken) {
                    const token = authResult.idToken;
                    const expiresInSec = get(authResult, 'expiresIn', 60 * 60 * 24);

                    return AuthenticationHttpService.loginV2({
                        token,
                        expiresInSec,
                    }).then(() => {
                        this.setAuthCookie('ok', expiresInSec);
                        resolve(authResult);
                    }).catch((error) => {
                        reject(error);
                    });
                }

                if (err) {
                    reject(err);
                }
                return null;
            });
        });
    }

    static register(userData) {
        return new Promise((resolve, reject) => {
            this.getAuth0().signup({
                connection: 'Username-Password-Authentication',
                email: userData.email,
                password: userData.email,
                user_metadata: {
                    phone: userData.phone,
                    name: userData.name,
                },
            }, (err) => {
                if (err) {
                    const { code } = err;

                    if (code === AUTH0_ERROR_CODE_USER_EXIST) {
                        this.login({
                            username: userData.email,
                            password: userData.email,
                        })
                            .then((data) => resolve(data))
                            .catch((error) => reject(error));
                    } else {
                        reject(err);
                    }
                } else {
                    this.login({
                        username: userData.email,
                        password: userData.email,
                    })
                        .then((data) => resolve(data))
                        .catch((error) => reject(error));
                }
            });
        });
    }

    static loginWith(connection) {
        return new Promise((resolve, reject) => {
            this.getAuth0().authorize({
                scope: 'openid email',
                connection,
                state: 'employer',
            }, (err, authResult) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(authResult);
                }
            });
        });
    }

    // eslint-disable-next-line consistent-return
    static async authorize({
        token,
        userType,
    }) {
        if (token) {
            try {
                BackendConnector.setHeaders({
                    'user-type': userType,
                });

                const user = await BackendConnector.get('user');

                return user;
            } catch (e) {
                Logger.error(e);
            }
        } else {
            throw new Error('Can\'t authorize if token not provided');
        }
    }

    static async logout() {
        await BackendConnector.get('logout');

        newRelicClearUserData();

        LocalStorageService.clear();

        AuthenticationHttpService.removeCookie('idToken');
        AuthenticationHttpService.removeCookie('authData');
        AuthenticationHttpService.removeCookie('userData');
        AuthenticationHttpService.removeCookie('origUserPrefs');
        AuthenticationHttpService.removeCookie('userprefs');
        AuthenticationHttpService.removeCookie('authResult');

        BackendConnector.setHeaders({});

        localStorage.removeItem('token');
        localStorage.removeItem('type');
    }

    static getCookie(name, customPostfix) {
        const postfix = isUndefined(customPostfix) ? cookiePostfix : customPostfix;
        const match = window.document.cookie.match(new RegExp(`(^| )${name}${postfix}=([^;]+)`));

        if (match) {
            return match[2];
        }
        return isUndefined(customPostfix) ? AuthenticationHttpService.getCookie(name, '') : null;
    }

    static async getIdToken() {
        try {
            const token = await BackendConnector.get('token');

            if (!token) {
                // for server side pdf render
                return this.getCookie('x-access-token');
            }

            return token;
        } catch (err) {
            Logger.error(err);
        }
        return null;
    }

    static getSsoHashAccessToken() {
        try {
            const query = new URLSearchParams(window.location.hash.substring(1));
            const result = query.get('access_token');

            return result;
        } catch (err) {
            Logger.error(err);
        }
        return null;
    }

    static getSSOAuthResult({ authData }) {
        return new Promise((resolve, reject) => {
            this.getAuth0(authData)
                .parseHash((err, authResult) => {
                    if (err) {
                        return reject(err);
                    }

                    return resolve(authResult);
                });
        });
    }

    static async getAuthData({ employerId, positionId }) {
        return BackendConnector.post('auth/data', { employerId, positionId });
    }

    static changePassword(email) {
        return new Promise((resolve, reject) => {
            this.dbAuth0.changePassword({
                connection: 'Username-Password-Authentication',
                email,
            }, (error, message) => {
                if (error) {
                    reject(error);

                    return;
                }

                resolve(message);
            });
        });
    }

    static async sendPasswordResetRequestNotification({ email }) {
        return BackendConnector.post('email/requestPasswordReset', { email });
    }
}
